<script setup>
import { reactive, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const uiFlags = useMapGetter('inboxes/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');

const isServerConfigured = window.chatwootConfig?.evolutionQrEnabled === 'true';
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

// Without a server configured by the super admin, the only way in is entering an existing instance by hand.
const manualSetup = ref(!isServerConfigured);
const showManualFields = computed(
  () => isSuperAdmin.value && manualSetup.value
);

const state = reactive({
  name: '',
  baseUrl: '',
  apiKey: '',
  instanceName: '',
});

const isHttpUrl = value => !value || /^https?:\/\//i.test(value);
const requiredIfManual = requiredIf(() => showManualFields.value);

const v$ = useVuelidate(
  {
    name: { required },
    baseUrl: { requiredIfManual, isHttpUrl },
    apiKey: { requiredIfManual },
    instanceName: { requiredIfManual },
  },
  state
);

const errorFor = (field, message) => (v$.value[field]?.$error ? message : '');

const formErrors = computed(() => ({
  name: errorFor('name', t('INBOX_MGMT.ADD.EVOLUTION.NAME.ERROR')),
  baseUrl: errorFor('baseUrl', t('INBOX_MGMT.ADD.EVOLUTION.BASE_URL.ERROR')),
  apiKey: errorFor('apiKey', t('INBOX_MGMT.ADD.EVOLUTION.API_KEY.ERROR')),
  instanceName: errorFor(
    'instanceName',
    t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.ERROR')
  ),
}));

const channelParams = () => {
  if (!showManualFields.value) return { type: 'evolution' };

  return {
    type: 'evolution',
    base_url: state.baseUrl.trim(),
    api_key: state.apiKey.trim(),
    instance_name: state.instanceName.trim(),
  };
};

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: state.name.trim(),
      channel: channelParams(),
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.EVOLUTION.API.ERROR_MESSAGE'));
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.EVOLUTION.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.EVOLUTION.DESC')"
    />

    <p
      v-if="!isServerConfigured && !isSuperAdmin"
      class="p-4 mb-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
    >
      {{ t('INBOX_MGMT.ADD.EVOLUTION.NOT_CONFIGURED') }}
    </p>

    <form
      v-else
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.name"
        :label="t('INBOX_MGMT.ADD.EVOLUTION.NAME.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.NAME.PLACEHOLDER')"
        :message="formErrors.name"
        :message-type="formErrors.name ? 'error' : 'info'"
        @blur="v$.name.$touch"
      />

      <label
        v-if="isSuperAdmin && isServerConfigured"
        class="flex gap-2 items-center text-sm text-n-slate-11"
      >
        <Switch v-model="manualSetup" />
        {{ t('INBOX_MGMT.ADD.EVOLUTION.MANUAL_SETUP') }}
      </label>

      <template v-if="showManualFields">
        <Input
          v-model="state.baseUrl"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.BASE_URL.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.BASE_URL.PLACEHOLDER')"
          :message="formErrors.baseUrl"
          :message-type="formErrors.baseUrl ? 'error' : 'info'"
          @blur="v$.baseUrl.$touch"
        />

        <Input
          v-model="state.apiKey"
          type="password"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.API_KEY.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.API_KEY.PLACEHOLDER')"
          :message="formErrors.apiKey"
          :message-type="formErrors.apiKey ? 'error' : 'info'"
          @blur="v$.apiKey.$touch"
        />

        <Input
          v-model="state.instanceName"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.PLACEHOLDER')"
          :message="
            formErrors.instanceName ||
            t('INBOX_MGMT.ADD.EVOLUTION.INSTANCE_NAME.HELP')
          "
          :message-type="formErrors.instanceName ? 'error' : 'info'"
          @blur="v$.instanceName.$touch"
        />
      </template>

      <div>
        <NextButton
          type="submit"
          :label="t('INBOX_MGMT.ADD.EVOLUTION.SUBMIT_BUTTON')"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
